import type { DailyCustomTrayButtons } from '@daily-co/daily-js';
import endSessionTrayIconSvg from '@/assets/icons/daily-tray-end-session.svg?raw';
import liveCaptionsTrayIconSvg from '@/assets/icons/daily-tray-live-captions.svg?raw';
import prontuarioTrayIconSvg from '@/assets/icons/daily-tray-prontuario.svg?raw';
import { PRONTUARIO_TRAY_ACTIVE_COLOR } from '@/constants/sessionCallDailyTheme';

export { PRONTUARIO_TRAY_ACTIVE_COLOR } from '@/constants/sessionCallDailyTheme';

export const PLUMAA_END_SESSION_TRAY_BUTTON_ID = 'plumaa-end-session';
export const PLUMAA_LIVE_CAPTIONS_TRAY_BUTTON_ID = 'plumaa-live-captions';
export const PLUMAA_PRONTUARIO_TRAY_BUTTON_ID = 'plumaa-prontuario';

const LIVE_CAPTIONS_TRAY_ACTIVE_COLOR = '#60A5FA';

const trayIconDataUri = (svg: string) =>
	`data:image/svg+xml,${encodeURIComponent(svg.trim())}`;

const END_SESSION_TRAY_ICON = trayIconDataUri(endSessionTrayIconSvg);
const LIVE_CAPTIONS_TRAY_ICON = trayIconDataUri(liveCaptionsTrayIconSvg);
const PRONTUARIO_TRAY_ICON = trayIconDataUri(prontuarioTrayIconSvg);

const END_SESSION_TRAY_COLOR = '#ef4444';

const LIVE_CAPTIONS_TRAY_BUTTON_SELECTOR = `#${PLUMAA_LIVE_CAPTIONS_TRAY_BUTTON_ID}, button[aria-label="Legenda"]`;
const PRONTUARIO_TRAY_BUTTON_SELECTOR = `#${PLUMAA_PRONTUARIO_TRAY_BUTTON_ID}, button[aria-label="Prontuário"]`;
const END_SESSION_TRAY_BUTTON_SELECTOR = `#${PLUMAA_END_SESSION_TRAY_BUTTON_ID}, button[aria-label="Encerrar"]`;

const LIVE_CAPTIONS_TRAY_ACTIVE_SELECTOR = `${LIVE_CAPTIONS_TRAY_BUTTON_SELECTOR}.btn-custom.active`;
const PRONTUARIO_TRAY_ACTIVE_SELECTOR = `${PRONTUARIO_TRAY_BUTTON_SELECTOR}.btn-custom.active`;

const LIVE_CAPTIONS_TRAY_INDICATOR_CSS = `
  ${LIVE_CAPTIONS_TRAY_BUTTON_SELECTOR} {
    position: relative;
  }

  ${LIVE_CAPTIONS_TRAY_ACTIVE_SELECTOR} img {
    background: ${LIVE_CAPTIONS_TRAY_ACTIVE_COLOR} !important;
    border-radius: 4px;
  }

  ${LIVE_CAPTIONS_TRAY_BUTTON_SELECTOR}::after {
    content: "";
    background: ${LIVE_CAPTIONS_TRAY_ACTIVE_COLOR};
    height: 2px;
    left: 0;
    position: absolute;
    top: calc(100% + 2px);
    transform: translateY(100%) scale(0);
    transition: transform 250ms;
    width: 100%;
    pointer-events: none;
  }

  ${LIVE_CAPTIONS_TRAY_ACTIVE_SELECTOR}::after {
    transform: translateY(0px) scale(1);
  }
`;

export const PRONTUARIO_TRAY_INDICATOR_CSS = `
  ${PRONTUARIO_TRAY_BUTTON_SELECTOR} {
    position: relative;
  }

  ${PRONTUARIO_TRAY_ACTIVE_SELECTOR} img {
    background: ${PRONTUARIO_TRAY_ACTIVE_COLOR} !important;
    border-radius: 4px;
  }

  ${PRONTUARIO_TRAY_BUTTON_SELECTOR}::after {
    content: "";
    background: ${PRONTUARIO_TRAY_ACTIVE_COLOR};
    height: 2px;
    left: 0;
    position: absolute;
    top: calc(100% + 2px);
    transform: translateY(100%) scale(0);
    transition: transform 250ms;
    width: 100%;
    pointer-events: none;
  }

  ${PRONTUARIO_TRAY_BUTTON_SELECTOR} .active-indicator {
    background: ${PRONTUARIO_TRAY_ACTIVE_COLOR};
    height: 2px;
    left: 0;
    position: absolute;
    top: calc(100% + 2px);
    transform: translateY(100%) scale(0);
    transition: transform 250ms;
    width: 100%;
  }

  ${PRONTUARIO_TRAY_ACTIVE_SELECTOR}::after,
  ${PRONTUARIO_TRAY_ACTIVE_SELECTOR} .active-indicator {
    transform: translateY(0px) scale(1);
  }

  ${PRONTUARIO_TRAY_BUTTON_SELECTOR}:has(.active-indicator)::after {
    display: none;
  }
`;

const END_SESSION_TRAY_STYLE_CSS = `
  ${END_SESSION_TRAY_BUTTON_SELECTOR} {
    color: ${END_SESSION_TRAY_COLOR} !important;
  }

  ${END_SESSION_TRAY_BUTTON_SELECTOR} span {
    color: ${END_SESSION_TRAY_COLOR} !important;
  }
`;

export const SESSION_CALL_TRAY_CUSTOM_CSS = `
  ${LIVE_CAPTIONS_TRAY_INDICATOR_CSS}
  ${PRONTUARIO_TRAY_INDICATOR_CSS}
  ${END_SESSION_TRAY_STYLE_CSS}
`;

type BuildProfessionalTrayButtonsOptions = {
	includeEndSession?: boolean;
	includeLiveCaptions?: boolean;
	includeProntuario?: boolean;
	isLiveCaptionsEnabled?: boolean;
	isProntuarioOpen?: boolean;
};

export function buildProfessionalTrayButtons(
	options: BuildProfessionalTrayButtonsOptions = {},
): DailyCustomTrayButtons {
	const {
		includeEndSession = false,
		includeLiveCaptions = false,
		includeProntuario = false,
		isLiveCaptionsEnabled = false,
		isProntuarioOpen = false,
	} = options;

	const buttons: DailyCustomTrayButtons = {};

	if (includeLiveCaptions) {
		buttons[PLUMAA_LIVE_CAPTIONS_TRAY_BUTTON_ID] = {
			iconPath: LIVE_CAPTIONS_TRAY_ICON,
			label: 'Legenda',
			tooltip: isLiveCaptionsEnabled
				? 'Ocultar legenda'
				: 'Exibir legenda',
			visualState: isLiveCaptionsEnabled ? 'active' : 'default',
		};
	}

	if (includeProntuario) {
		buttons[PLUMAA_PRONTUARIO_TRAY_BUTTON_ID] = {
			iconPath: PRONTUARIO_TRAY_ICON,
			label: 'Prontuário',
			tooltip: isProntuarioOpen
				? 'Fechar prontuário'
				: 'Abrir prontuário',
			visualState: isProntuarioOpen ? 'active' : 'default',
		};
	}

	if (includeEndSession) {
		buttons[PLUMAA_END_SESSION_TRAY_BUTTON_ID] = {
			iconPath: END_SESSION_TRAY_ICON,
			label: 'Encerrar',
			tooltip: 'Encerrar sessão para todos',
		};
	}

	return buttons;
}

export function buildEndSessionTrayButtons(): DailyCustomTrayButtons {
	return buildProfessionalTrayButtons({ includeEndSession: true });
}
